import React, { useState, useEffect, useRef, useCallback } from "react";
import { API_UserNotifications } from "../../../src/apidefs";
import { useUserStatus, getAudioElement } from "../state";
import { api } from "../api";
import { RLink } from "../DesignSystem/RLink";
import { GoldCoin } from "../Toolbar/GoldCoin";
import { SilverCoin } from "../Toolbar/SilverCoin";
import {
  SunIcon,
  MoonIcon,
  PencilIcon,
  UserIcon,
  StarIcon,
  WrenchScrewdriverIcon,
  ChevronDownIcon,
  PlayIcon,
  PauseIcon,
  TrashIcon,
  SpeakerWaveIcon,
  SpeakerXMarkIcon,
  ForwardIcon,
  BackwardIcon,
  ChatBubbleLeftRightIcon,
} from "@heroicons/react/24/solid";
import { SmallButton } from "../DesignSystem/SmallButton";
import { Tooltip } from "../DesignSystem/Tooltip";
import { TestIds } from "../testIds";

const SYNC_LISTEN_VOLUME_KEY = "syncListenVolume";

const secondsToReadableString = (totalSeconds: number) => {
  if (isNaN(totalSeconds)) {
    totalSeconds = 0;
  }
  const minutes = String(Math.floor(totalSeconds / 60));
  let seconds = String(Math.floor(totalSeconds % 60));
  if (seconds.length === 1) {
    seconds = "0" + seconds;
  }
  return `${minutes}:${seconds}`;
};

type Theme = "light" | "dark";
const THEME_STORAGE_KEY = "chips-theme";

export type SyncConnectionStatus = "live" | "stale" | "stopped";

type SyncListenHeaderProps = {
  compoName: string;
  isSyncRunning: boolean;
  isSyncStarted: boolean;
  connectionStatus: SyncConnectionStatus;
  syncSecondsIntoEntry: number;
  lastSseEventTime: number | null;
  onStartSync: () => void;
  onPauseSync: () => void;
  onDeleteSync: () => void;
  onNextEntry: () => void;
  onPrevEntry: () => void;
  onSetDiscordSync: () => void;
  isSyncLoading: boolean;
  syncedToDiscordChannel: boolean;
};

export function SyncListenHeader({
  compoName,
  isSyncRunning,
  isSyncStarted,
  connectionStatus,
  syncSecondsIntoEntry,
  lastSseEventTime,
  onStartSync,
  onPauseSync,
  onDeleteSync,
  onNextEntry,
  onPrevEntry,
  onSetDiscordSync,
  isSyncLoading,
  syncedToDiscordChannel,
}: SyncListenHeaderProps): JSX.Element {
  const { user: userState } = useUserStatus();
  const isLoggedIn = userState.type === "logged-in";
  const user = isLoggedIn ? userState.user : null;
  const isAdmin = isLoggedIn && userState.user.admin;

  const [notifications, setNotifications] =
    useState<API_UserNotifications | null>(null);
  const [theme, setTheme] = useState<Theme>("dark");
  const [userDropdownOpen, setUserDropdownOpen] = useState(false);
  const [syncDropdownVisible, setSyncDropdownVisible] = useState(false);
  const [volumePopupVisible, setVolumePopupVisible] = useState(false);

  // Audio playback state - starts muted
  const [volume, setVolume] = useState(0);
  const [rememberedVolume, setRememberedVolume] = useState(() => {
    const saved = localStorage.getItem(SYNC_LISTEN_VOLUME_KEY);
    return saved !== null ? parseFloat(saved) : 0.7;
  });
  const [secondsPlayed, setSecondsPlayed] = useState(0);
  const [secondsTotal, setSecondsTotal] = useState(0);
  const [playedPercentage, setPlayedPercentage] = useState(0);
  const volumeInputRef = useRef<HTMLInputElement>(null);

  // Update playback display from sync time (interpolating between SSE updates)
  useEffect(() => {
    const updateDisplay = () => {
      const audio = getAudioElement();
      const duration = audio?.duration || 0;
      setSecondsTotal(duration);

      // Interpolate time: sync seconds + time since last SSE event
      let currentSeconds = syncSecondsIntoEntry;
      if (lastSseEventTime !== null && isSyncRunning) {
        const timeSinceLastEvent = (Date.now() - lastSseEventTime) / 1000;
        currentSeconds = syncSecondsIntoEntry + timeSinceLastEvent;
      }

      // Cap at duration so we don't show time past the song length
      if (duration > 0 && currentSeconds > duration) {
        currentSeconds = duration;
      }

      setSecondsPlayed(currentSeconds);
      if (duration > 0) {
        setPlayedPercentage(Math.min((currentSeconds / duration) * 100, 100));
      }
    };
    const interval = setInterval(updateDisplay, 100);
    updateDisplay();
    return () => clearInterval(interval);
  }, [syncSecondsIntoEntry, lastSseEventTime, isSyncRunning]);

  // Sync volume to audio element
  useEffect(() => {
    const audio = getAudioElement();
    if (!audio) return;
    audio.volume = volume;
    if (volumeInputRef.current) {
      volumeInputRef.current.value = String(volume * 100);
    }
  }, [volume]);

  // Save remembered volume to localStorage
  useEffect(() => {
    if (rememberedVolume > 0) {
      localStorage.setItem(SYNC_LISTEN_VOLUME_KEY, String(rememberedVolume));
    }
  }, [rememberedVolume]);

  const handleVolumeChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const vol = Number(e.currentTarget.value) / 100;
      const newVol = vol <= 0.03 ? 0 : vol;
      setVolume(newVol);
      if (newVol > 0) {
        setRememberedVolume(newVol);
      }
    },
    []
  );

  const toggleMute = useCallback(() => {
    if (volume > 0) {
      setRememberedVolume(volume);
      setVolume(0);
    } else {
      // If volume is 0 and no remembered volume, jump to 50%
      setVolume(rememberedVolume > 0 ? rememberedVolume : 0.5);
    }
  }, [volume, rememberedVolume]);

  const handleSeekClick = (
    e: React.MouseEvent<HTMLDivElement> | React.TouchEvent<HTMLDivElement>
  ) => {
    const audio = getAudioElement();
    const seek = e.currentTarget;
    if (!audio || !seek || !audio.duration) return;
    const rect = seek.getBoundingClientRect();
    const clientX =
      "touches" in e
        ? (e.touches[0]?.clientX ?? e.changedTouches[0]?.clientX)
        : e.clientX;
    const where = clientX - rect.left;
    const total = rect.width;
    audio.currentTime = (where / total) * audio.duration;
  };

  const isMuted = volume === 0;

  // Fetch notifications
  useEffect(() => {
    const fetchNotifications = async () => {
      const result = await api.get("/api/notifications");
      setNotifications(result);
    };
    fetchNotifications();
  }, []);

  // Initialize theme - must also apply the class since NavToolbar is hidden
  useEffect(() => {
    const root = document.documentElement;
    const stored = window.localStorage.getItem(THEME_STORAGE_KEY);
    if (stored === "light" || stored === "dark") {
      setTheme(stored);
      root.classList.remove("light", "dark");
      root.classList.add(stored);
    } else if (
      window.matchMedia &&
      window.matchMedia("(prefers-color-scheme: light)").matches
    ) {
      setTheme("light");
      root.classList.remove("light", "dark");
      root.classList.add("light");
    } else {
      // Default to dark
      root.classList.remove("light", "dark");
      root.classList.add("dark");
    }
  }, []);

  const applyTheme = (newTheme: Theme) => {
    const root = document.documentElement;
    root.classList.remove("light", "dark");
    root.classList.add(newTheme);
    window.localStorage.setItem(THEME_STORAGE_KEY, newTheme);
    setTheme(newTheme);
  };

  const toggleTheme = () => {
    applyTheme(theme === "dark" ? "light" : "dark");
  };

  const logout = async () => {
    await api.get("/api/logout");
  };

  // Close dropdowns when clicking outside
  useEffect(() => {
    if (!userDropdownOpen && !syncDropdownVisible && !volumePopupVisible)
      return;

    const handleClickOutside = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      if (userDropdownOpen && !target.closest("[data-user-dropdown]")) {
        setUserDropdownOpen(false);
      }
      if (syncDropdownVisible && !target.closest("[data-sync-dropdown]")) {
        setSyncDropdownVisible(false);
      }
      if (volumePopupVisible && !target.closest("[data-volume-popup]")) {
        setVolumePopupVisible(false);
      }
    };

    document.addEventListener("click", handleClickOutside);
    return () => document.removeEventListener("click", handleClickOutside);
  }, [userDropdownOpen, syncDropdownVisible, volumePopupVisible]);

  const hasNotifications =
    notifications &&
    notifications.unread_count &&
    notifications.unread_count > 0;

  return (
    <div className="relative z-50 flex-shrink-0 border-b border-slate-200 bg-white/80 backdrop-blur dark:border-white/10 dark:bg-slate-900/95">
      <div className="flex w-full items-center justify-between gap-2 px-3 py-2 md:gap-3 md:px-4">
        {/* Left side: Logo */}
        <RLink
          white
          noUnderline
          to={isLoggedIn ? "/feed" : "/"}
          className="group flex shrink-0 items-center gap-2 text-lg font-medium tracking-wide text-slate-800 hover:text-slate-900 dark:text-white/90 dark:hover:text-white"
        >
          <i
            className="fa fa-play text-chips-gold/90 group-hover:text-chips-gold"
            aria-hidden="true"
          ></i>
          <span>chips</span>
        </RLink>

        {/* Middle: Sync listen state + playback */}
        <div className="flex flex-1 flex-col items-center justify-center gap-1">
          {/* Title row */}
          <div className="flex items-center gap-2 text-sm">
            {/* Discord sync indicator - admin only */}
            {isAdmin && (
              <Tooltip
                element={
                  <span
                    className={`inline-block h-2.5 w-2.5 rounded-full ${
                      syncedToDiscordChannel
                        ? "bg-emerald-500"
                        : "bg-slate-400 dark:bg-slate-600"
                    }`}
                  />
                }
                tooltip={
                  syncedToDiscordChannel
                    ? "Syncing to Discord channel"
                    : "Not syncing to Discord channel"
                }
              />
            )}
            {/* Connection status indicator */}
            <Tooltip
              element={
                <span
                  data-testid={TestIds.SyncListenConnectionStatus}
                  data-status={connectionStatus}
                  className={`inline-block h-2.5 w-2.5 rounded-full ${
                    connectionStatus === "live"
                      ? "animate-pulse bg-emerald-500"
                      : connectionStatus === "stale"
                        ? "bg-amber-500"
                        : "bg-slate-400 dark:bg-slate-600"
                  }`}
                />
              }
              tooltip={
                connectionStatus === "live"
                  ? "Live"
                  : connectionStatus === "stale"
                    ? "Connection may be lost"
                    : "Not started"
              }
            />
            <span className="font-semibold text-slate-800 dark:text-white">
              {compoName} Sync
            </span>
            {isAdmin && (
              <>
                <span className="text-slate-400 dark:text-slate-500">·</span>
                <div className="relative" data-sync-dropdown>
                  <SmallButton
                    variant="mod"
                    onClick={() => setSyncDropdownVisible((prev) => !prev)}
                    icon={<ChevronDownIcon className="h-4 w-4" />}
                  >
                    Sync
                  </SmallButton>
                  {syncDropdownVisible && (
                    <div className="absolute left-1/2 top-full z-50 mt-1 min-w-[140px] -translate-x-1/2 overflow-hidden rounded-lg border border-slate-200 bg-white shadow-lg dark:border-slate-700 dark:bg-slate-800">
                      <button
                        data-testid={TestIds.SyncListenStartButton}
                        onClick={() => {
                          setSyncDropdownVisible(false);
                          onStartSync();
                        }}
                        disabled={isSyncLoading || isSyncRunning}
                        className="flex w-full items-center gap-2 px-3 py-2 text-left text-sm text-slate-700 hover:bg-slate-100 disabled:cursor-not-allowed disabled:opacity-50 dark:text-slate-200 dark:hover:bg-slate-700"
                      >
                        <PlayIcon className="h-4 w-4" />
                        {isSyncStarted ? "Resume" : "Start"}
                      </button>
                      <button
                        data-testid={TestIds.SyncListenPauseButton}
                        onClick={() => {
                          setSyncDropdownVisible(false);
                          onPauseSync();
                        }}
                        disabled={isSyncLoading || !isSyncRunning}
                        className="flex w-full items-center gap-2 px-3 py-2 text-left text-sm text-slate-700 hover:bg-slate-100 disabled:cursor-not-allowed disabled:opacity-50 dark:text-slate-200 dark:hover:bg-slate-700"
                      >
                        <PauseIcon className="h-4 w-4" />
                        Pause
                      </button>
                      <div className="my-1 border-t border-slate-200 dark:border-slate-700" />
                      <button
                        onClick={() => {
                          setSyncDropdownVisible(false);
                          onPrevEntry();
                        }}
                        disabled={isSyncLoading || !isSyncStarted}
                        className="flex w-full items-center gap-2 px-3 py-2 text-left text-sm text-slate-700 hover:bg-slate-100 disabled:cursor-not-allowed disabled:opacity-50 dark:text-slate-200 dark:hover:bg-slate-700"
                      >
                        <BackwardIcon className="h-4 w-4" />
                        Previous
                      </button>
                      <button
                        data-testid={TestIds.SyncListenNextButton}
                        onClick={() => {
                          setSyncDropdownVisible(false);
                          onNextEntry();
                        }}
                        disabled={isSyncLoading || !isSyncStarted}
                        className="flex w-full items-center gap-2 px-3 py-2 text-left text-sm text-slate-700 hover:bg-slate-100 disabled:cursor-not-allowed disabled:opacity-50 dark:text-slate-200 dark:hover:bg-slate-700"
                      >
                        <ForwardIcon className="h-4 w-4" />
                        Next
                      </button>
                      <div className="my-1 border-t border-slate-200 dark:border-slate-700" />
                      <button
                        onClick={() => {
                          setSyncDropdownVisible(false);
                          onSetDiscordSync();
                        }}
                        disabled={isSyncLoading || syncedToDiscordChannel}
                        className="flex w-full items-center gap-2 px-3 py-2 text-left text-sm text-slate-700 hover:bg-slate-100 disabled:cursor-not-allowed disabled:opacity-50 dark:text-slate-200 dark:hover:bg-slate-700"
                      >
                        <ChatBubbleLeftRightIcon className="h-4 w-4" />
                        Sync chat to Discord
                      </button>
                      <div className="my-1 border-t border-slate-200 dark:border-slate-700" />
                      <button
                        onClick={() => {
                          setSyncDropdownVisible(false);
                          onDeleteSync();
                        }}
                        disabled={isSyncLoading}
                        className="flex w-full items-center gap-2 px-3 py-2 text-left text-sm text-red-600 hover:bg-red-50 disabled:cursor-not-allowed disabled:opacity-50 dark:text-red-400 dark:hover:bg-red-900/20"
                      >
                        <TrashIcon className="h-4 w-4" />
                        Delete
                      </button>
                    </div>
                  )}
                </div>
              </>
            )}
          </div>

          {/* Progress bar row with volume */}
          <div className="flex w-full max-w-md items-center gap-2">
            <span className="text-[0.65rem] tabular-nums text-slate-500 dark:text-white/60">
              {secondsToReadableString(secondsPlayed)}
            </span>
            <div
              onClick={handleSeekClick}
              onTouchStart={handleSeekClick}
              className="relative h-4 flex-1 cursor-pointer flex items-center"
            >
              <div className="relative h-1 w-full rounded-full bg-slate-200 dark:bg-white/20">
                <div
                  className="absolute inset-y-0 left-0 rounded-full bg-chips-gold transition-[width] duration-100"
                  style={{ width: playedPercentage + "%" }}
                />
              </div>
            </div>
            <span className="text-[0.65rem] tabular-nums text-slate-500 dark:text-white/60">
              {secondsToReadableString(secondsTotal)}
            </span>

            {/* Volume control - outer div has padding for expanded hover zone */}
            <div
              className="-m-3 p-3"
              data-volume-popup
              onMouseEnter={() => setVolumePopupVisible(true)}
              onMouseLeave={() => setVolumePopupVisible(false)}
            >
              <div className="relative">
                <button
                  type="button"
                  onClick={toggleMute}
                  className="inline-flex h-6 w-6 cursor-pointer items-center justify-center rounded text-slate-500 transition hover:bg-slate-200 hover:text-slate-700 dark:text-white/60 dark:hover:bg-white/10 dark:hover:text-white"
                  aria-label="Volume"
                >
                  {isMuted ? (
                    <SpeakerXMarkIcon className="h-4 w-4" />
                  ) : (
                    <SpeakerWaveIcon className="h-4 w-4" />
                  )}
                </button>
              {volumePopupVisible && (
                <>
                <div className="absolute top-full left-1/2 z-50 mt-2 -translate-x-1/2 rounded-lg border border-slate-200 bg-white p-3 shadow-lg dark:border-slate-700 dark:bg-slate-800">
                  <div className="flex flex-col items-center gap-2">
                    <input
                      type="range"
                      min="0"
                      max="100"
                      value={volume * 100}
                      onChange={handleVolumeChange}
                      ref={volumeInputRef}
                      className="h-24 w-1.5 cursor-pointer appearance-none rounded-full bg-slate-200 accent-chips-gold dark:bg-white/20"
                      style={{
                        writingMode: "vertical-lr",
                        direction: "rtl",
                      }}
                      aria-label="Volume"
                    />
                    <button
                      type="button"
                      onClick={toggleMute}
                      className="inline-flex h-6 w-6 items-center justify-center rounded text-slate-500 transition hover:bg-slate-100 hover:text-slate-700 dark:text-white/60 dark:hover:bg-white/10 dark:hover:text-white"
                      aria-label={isMuted ? "Unmute" : "Mute"}
                    >
                      {isMuted ? (
                        <SpeakerXMarkIcon className="h-4 w-4" />
                      ) : (
                        <SpeakerWaveIcon className="h-4 w-4" />
                      )}
                    </button>
                  </div>
                </div>
                </>
              )}
              </div>
            </div>
          </div>
        </div>

        {/* Right side: Notifications + User */}
        {isLoggedIn && user ? (
          <div className="flex items-center gap-2 text-sm text-slate-900 dark:text-white md:gap-3">
            {/* Notifications */}
            <RLink
              white
              noUnderline
              className="inline-flex items-center gap-1.5 rounded-lg px-3 py-2 text-sm transition hover:bg-slate-100 dark:hover:bg-white/10"
              to="/notifications"
            >
              <i
                className={`fa fa-bell-o ${hasNotifications ? "text-chips-gold" : ""}`}
                aria-hidden="true"
              ></i>
              {hasNotifications && (
                <span className="font-semibold text-chips-gold">
                  {notifications.unread_count}
                </span>
              )}
            </RLink>

            {/* Username dropdown */}
            <div className="relative flex items-center" data-user-dropdown>
              <button
                onClick={() => setUserDropdownOpen((prev) => !prev)}
                className="flex cursor-pointer items-center gap-3 rounded-lg px-3 py-2 text-sm font-normal transition hover:bg-slate-100 dark:hover:bg-white/10"
              >
                <div className="flex flex-col items-start gap-0.5">
                  <div className="flex items-center gap-1.5">
                    <span className="font-medium tracking-wide dark:text-white">
                      {user.username}
                    </span>
                    <span className="text-xs font-light uppercase text-slate-500 dark:text-white/70">
                      (LV {user.level})
                    </span>
                  </div>
                  <div className="flex items-center gap-1 text-xs text-slate-500 dark:text-white/70">
                    {user.gold} <GoldCoin /> {user.silver} <SilverCoin />
                  </div>
                </div>
                <i
                  className={`fa fa-chevron-down text-xs transition-transform ${
                    userDropdownOpen ? "rotate-180" : ""
                  }`}
                  aria-hidden="true"
                ></i>
              </button>

              {userDropdownOpen && (
                <div className="absolute right-0 top-full z-50 mt-2 min-w-[180px] overflow-hidden rounded-lg border border-slate-200 bg-white shadow-lg dark:border-white/10 dark:bg-slate-800">
                  <RLink
                    white
                    noUnderline
                    to="/edit/profile"
                    className="flex w-full items-center gap-2 px-4 py-2 text-left text-sm hover:bg-slate-100 dark:hover:bg-white/10"
                    onClick={() => setUserDropdownOpen(false)}
                  >
                    <PencilIcon className="h-4 w-4" />
                    Edit profile
                  </RLink>
                  <RLink
                    white
                    noUnderline
                    to={`/user/${user.username}`}
                    className="flex w-full items-center gap-2 px-4 py-2 text-left text-sm hover:bg-slate-100 dark:hover:bg-white/10"
                    onClick={() => setUserDropdownOpen(false)}
                  >
                    <UserIcon className="h-4 w-4" />
                    View profile
                  </RLink>
                  <RLink
                    white
                    noUnderline
                    to="/explain-xp"
                    className="flex w-full items-center gap-2 px-4 py-2 text-left text-sm hover:bg-slate-100 dark:hover:bg-white/10"
                    onClick={() => setUserDropdownOpen(false)}
                  >
                    <StarIcon className="h-4 w-4" />
                    Learn about XP
                  </RLink>
                  {user.admin && (
                    <RLink
                      white
                      noUnderline
                      to="/admin/support"
                      className="flex w-full items-center gap-2 px-4 py-2 text-left text-sm hover:bg-slate-100 dark:hover:bg-white/10"
                      onClick={() => setUserDropdownOpen(false)}
                    >
                      <WrenchScrewdriverIcon className="h-4 w-4" />
                      Support
                    </RLink>
                  )}
                  <div className="my-1 border-t border-slate-200 dark:border-white/10" />
                  <button
                    onClick={() => {
                      toggleTheme();
                      setUserDropdownOpen(false);
                    }}
                    className="flex w-full items-center gap-2 px-4 py-2 text-left text-sm hover:bg-slate-100 dark:hover:bg-white/10"
                  >
                    {theme === "dark" ? (
                      <SunIcon className="h-4 w-4" />
                    ) : (
                      <MoonIcon className="h-4 w-4" />
                    )}
                    {theme === "dark" ? "Light mode" : "Dark mode"}
                  </button>
                  <button
                    className="flex w-full items-center gap-2 px-4 py-2 text-left text-sm text-slate-900 hover:bg-slate-100 dark:text-white dark:hover:bg-white/10"
                    onClick={() => {
                      logout();
                      setUserDropdownOpen(false);
                    }}
                  >
                    <i className="fa fa-power-off w-4" aria-hidden="true"></i>
                    Log out
                  </button>
                </div>
              )}
            </div>
          </div>
        ) : (
          <div className="flex items-center gap-2 text-sm font-medium uppercase tracking-wide text-slate-700 dark:text-white">
            <RLink white to="/login">
              log in
            </RLink>
            <span className="text-slate-400 dark:text-white/30">|</span>
            <RLink white to="/register">
              sign up
            </RLink>
            <button
              onClick={toggleTheme}
              className="rounded-full border border-slate-200 p-2 text-xs uppercase tracking-wide text-slate-700 transition hover:bg-slate-100 dark:border-white/15 dark:text-white dark:hover:bg-white/10"
              aria-label="Toggle theme"
            >
              {theme === "dark" ? (
                <SunIcon className="h-4 w-4" />
              ) : (
                <MoonIcon className="h-4 w-4" />
              )}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
